import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { errMsg } from "../../lib/api";
import { useAdmin } from "../../pages/admin/AdminApp";
import { ChevronRight, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";

export function Fold({ title, meta, children, testid, defaultOpen }) {
  return (
    <details className="group border rounded-lg bg-white" open={defaultOpen || undefined} data-testid={testid}>
      <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-3 text-sm font-medium hover:bg-stone-50 rounded-lg" data-testid={testid && `${testid}-toggle`}>
        <span className="flex items-center gap-2 min-w-0"><ChevronRight size={15} className="shrink-0 transition-transform group-open:rotate-90" /><span className="truncate">{title}</span></span>
        {meta && <span className="text-xs text-stone-400 truncate max-w-[50%]">{meta}</span>}
      </summary>
      <div className="px-4 pb-4 pt-3 space-y-4 border-t">{children}</div>
    </details>
  );
}

const NESTED = new Set(["hide", "socials", "socials_campaign", "socials_split"]);
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const diffObj = (a = {}, b = {}) => Object.fromEntries(Object.entries(a || {}).filter(([k, v]) => !same(v, (b || {})[k])));

export function useSection(section) {
  const { settings, saveSection } = useAdmin();
  const [f, setF] = useState(() => settings[section] || {});
  const [busy, setBusy] = useState(false);
  const serverJson = JSON.stringify(settings[section] || {});
  const prev = useRef(serverJson);
  useEffect(() => {
    const old = JSON.parse(prev.current), nu = JSON.parse(serverJson);
    prev.current = serverJson;
    setF((p) => {
      const out = { ...nu };
      for (const k in p) {
        if (same(p[k], old[k])) continue;
        out[k] = NESTED.has(k) ? { ...(nu[k] || {}), ...diffObj(p[k], old[k]) } : p[k];
      }
      return out;
    });
  }, [serverJson]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? (v.target.type === "checkbox" ? v.target.checked : v.target.value) : v }));
  const save = async (override) => {
    setBusy(true);
    const base = settings[section] || {};
    const body = override || Object.fromEntries(Object.entries(f).filter(([k, v]) => !same(v, base[k])).map(([k, v]) => [k, NESTED.has(k) ? diffObj(v, base[k]) : v]));
    try { await saveSection(section, body); toast.success("Ayarlar kaydedildi"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const setSave = (k) => (v) => { setF((p) => ({ ...p, [k]: v })); save({ [k]: v }); };
  return { f, setF, set, save, busy, setSave };
}

export function Card({ title, sub, children, onSave, busy, testid, defaultOpen = false }) {
  const [open, setOpen] = useState(!!defaultOpen);
  // Allow parents to force-open the card later (e.g. when a prefill arrives).
  // We only react to defaultOpen flipping from false→true so users keeping a
  // card manually closed are not fought.
  useEffect(() => {
    if (defaultOpen) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOpen]);
  // If no title is provided, render in classic (non-collapsible) mode so
  // layouts that use <Card> purely as a styled wrapper keep working.
  if (!title) {
    return (
      <section className="bg-white border rounded-xl p-6 mb-6" data-testid={testid}>
        <div className="space-y-5">{children}</div>
        {onSave && (
          <div className="mt-6 pt-5 border-t flex justify-end">
            <Button onClick={() => onSave()} disabled={busy} data-testid={`${testid}-save`}>{busy ? "Kaydediliyor…" : "Kaydet"}</Button>
          </div>
        )}
      </section>
    );
  }
  return (
    <section className="bg-white border rounded-xl mb-6 overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full px-6 py-4 flex items-start justify-between gap-4 text-left hover:bg-stone-50/60 transition"
        data-testid={testid ? `${testid}-toggle` : undefined}
      >
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-lg leading-snug">{title}</h2>
          {sub && <p className="text-sm text-stone-500 mt-1 leading-snug">{sub}</p>}
        </div>
        <ChevronDown
          size={18}
          className={`text-stone-400 shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? (
        <div className="px-6 pb-6 pt-1 border-t border-stone-100 space-y-5">
          <div className="pt-4 space-y-5">{children}</div>
          {onSave && (
            <div className="mt-6 pt-5 border-t flex justify-end">
              <Button onClick={() => onSave()} disabled={busy} data-testid={`${testid}-save`}>{busy ? "Kaydediliyor…" : "Kaydet"}</Button>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}

export function Vis({ f, set, k, label = "Sitede göster" }) {
  const H = f.hide || {};
  return (
    <label className="flex items-center gap-2 text-xs text-stone-600 whitespace-nowrap shrink-0">
      <Switch checked={!H[k]} onCheckedChange={(v) => set("hide")({ ...H, [k]: !v })} data-testid={`vis-${k}`} />{label}
    </label>
  );
}

export function Row({ f, set, k, children }) {
  const off = (f.hide || {})[k];
  return (
    <div className="flex flex-col sm:flex-row gap-3 sm:items-start" data-testid={`vis-row-${k}`}>
      <div className={`flex-1 transition-opacity ${off ? "opacity-40" : ""}`}>{children}</div>
      <div className="sm:pt-7"><Vis f={f} set={set} k={k} /></div>
    </div>
  );
}

export function VisGroup({ title, f, set, items }) {
  return (
    <div className="rounded-lg border bg-stone-50 p-4">
      <p className="text-sm font-medium mb-3">{title}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map(([k, l]) => <Vis key={k} f={f} set={set} k={k} label={l} />)}
      </div>
    </div>
  );
}

